/**
 * DungeonMap - Underground Crypt & Ancient Flame Dragon Shrine
 * Faithfully ported from the refined prototype specifications.
 * Positioned seamlessly on the northern upper sector of the world (Y: -32 to -2).
 */

import { EntityState } from '../../core/Types';

export type DungeonTileType = 'crypt_floor' | 'lava_floor' | 'carpet_red';

export interface DungeonPropConfig {
  id: string;
  type: 'crypt_wall' | 'torch_pillar' | 'bone_pile' | 'dungeon_gate' | 'loot_chest';
  name: string;
  x: number;
  y: number;
}

// 1. Grid Tile Map for Dungeon (Fast lookup: Map<"x,y", DungeonTileType>)
const dungeonTiles = new Map<string, DungeonTileType>();
const dungeonProps: DungeonPropConfig[] = [];

function key(x: number, y: number): string {
  return `${x},${y}`;
}

export function isDungeonTile(x: number, y: number): DungeonTileType | null {
  return dungeonTiles.get(key(x, y)) || null;
}

export function getDungeonProps(): DungeonPropConfig[] {
  return dungeonProps;
}

// Build Dungeon Map Data
export function initDungeonData(): void {
  dungeonTiles.clear();
  dungeonProps.length = 0;

  // === 1. Entrance Hall: Y: -7 to -3, X: 7 to 13 ===
  for (let y = -7; y <= -3; y++) {
    for (let x = 7; x <= 13; x++) {
      dungeonTiles.set(key(x, y), 'crypt_floor');
    }
  }
  // Connect pathway to village at (10, -2)
  dungeonTiles.set(key(10, -2), 'crypt_floor');
  dungeonTiles.set(key(9, -2), 'crypt_floor');
  dungeonTiles.set(key(11, -2), 'crypt_floor');

  // Entrance props
  dungeonProps.push({ id: 'prop_dungeon_gate', type: 'dungeon_gate', name: '고딕 흑요석 던전 관문', x: 10, y: -2 });
  dungeonProps.push({ id: 'prop_torch_in_1', type: 'torch_pillar', name: '횃불 석주', x: 7, y: -5 });
  dungeonProps.push({ id: 'prop_torch_in_2', type: 'torch_pillar', name: '횃불 석주', x: 13, y: -5 });

  // === 2. Central Corridor: Y: -16 to -8, X: 9 to 11 ===
  for (let y = -16; y <= -8; y++) {
    for (let x = 9; x <= 11; x++) {
      dungeonTiles.set(key(x, y), 'crypt_floor');
    }
  }
  dungeonProps.push({ id: 'prop_torch_corridor_1', type: 'torch_pillar', name: '횃불 석주', x: 8, y: -12 });
  dungeonProps.push({ id: 'prop_torch_corridor_2', type: 'torch_pillar', name: '횃불 석주', x: 12, y: -12 });

  // === 3. West Chamber (Skeleton Archers): Y: -15 to -9, X: 0 to 8 ===
  for (let y = -15; y <= -9; y++) {
    for (let x = 0; x <= 8; x++) {
      dungeonTiles.set(key(x, y), 'crypt_floor');
    }
  }
  dungeonProps.push({ id: 'prop_bones_west_1', type: 'bone_pile', name: '해골 유골 더미', x: 1, y: -14 });
  dungeonProps.push({ id: 'prop_bones_west_2', type: 'bone_pile', name: '해골 유골 더미', x: 1, y: -10 });

  // === 4. East Chamber (Crypt Ghouls): Y: -15 to -9, X: 12 to 20 ===
  for (let y = -15; y <= -9; y++) {
    for (let x = 12; x <= 20; x++) {
      dungeonTiles.set(key(x, y), 'crypt_floor');
    }
  }
  dungeonProps.push({ id: 'prop_bones_east_1', type: 'bone_pile', name: '해골 유골 더미', x: 19, y: -14 });
  dungeonProps.push({ id: 'prop_bones_east_2', type: 'bone_pile', name: '해골 유골 더미', x: 19, y: -10 });

  // === 5. Great Hall of Guardians: Y: -22 to -17, X: 6 to 14 ===
  for (let y = -22; y <= -17; y++) {
    for (let x = 6; x <= 14; x++) {
      dungeonTiles.set(key(x, y), 'crypt_floor');
    }
  }
  dungeonProps.push({ id: 'prop_torch_hall_1', type: 'torch_pillar', name: '횃불 석주', x: 6, y: -20 });
  dungeonProps.push({ id: 'prop_torch_hall_2', type: 'torch_pillar', name: '횃불 석주', x: 14, y: -20 });

  // === 6. North Dragon Lair: Y: -31 to -23, X: 2 to 18 ===
  for (let y = -31; y <= -23; y++) {
    for (let x = 2; x <= 18; x++) {
      dungeonTiles.set(key(x, y), 'lava_floor');
    }
  }
  // Royal Red Carpet on center pathway
  for (let y = -27; y <= -23; y++) {
    dungeonTiles.set(key(10, y), 'carpet_red');
  }
  dungeonProps.push({ id: 'prop_torch_dragon_1', type: 'torch_pillar', name: '횃불 석주', x: 3, y: -29 });
  dungeonProps.push({ id: 'prop_torch_dragon_2', type: 'torch_pillar', name: '횃불 석주', x: 17, y: -29 });
  dungeonProps.push({ id: 'prop_bones_dragon_1', type: 'bone_pile', name: '해골 유골 더미', x: 5, y: -25 });
  dungeonProps.push({ id: 'prop_bones_dragon_2', type: 'bone_pile', name: '해골 유골 더미', x: 15, y: -25 });
  // Epic Dragon Loot Chest behind the altar
  dungeonProps.push({ id: 'prop_loot_chest', type: 'loot_chest', name: '전설의 화염룡 보물 상자', x: 10, y: -30 });

  // === 7. Surround all dungeon tiles with Dark Gothic Crypt Walls ===
  const wallPositions = new Set<string>();
  const dirs = [
    [-1, 0], [1, 0], [0, -1], [0, 1],
    [-1, -1], [-1, 1], [1, -1], [1, 1]
  ];

  dungeonTiles.forEach((_, tKey) => {
    const [tx, ty] = tKey.split(',').map(Number);
    for (const [dx, dy] of dirs) {
      const nx = tx + dx;
      const ny = ty + dy;
      const nKey = key(nx, ny);
      // Don't place wall on existing floor or southward pathway into village
      if (!dungeonTiles.has(nKey) && ny <= -2) {
        if (!(nx === 10 && ny === -1)) {
          wallPositions.add(nKey);
        }
      }
    }
  });

  let wallIdx = 1;
  wallPositions.forEach((wKey) => {
    const [wx, wy] = wKey.split(',').map(Number);
    dungeonProps.push({
      id: `prop_crypt_wall_${wallIdx++}`,
      type: 'crypt_wall',
      name: '고딕 크립트 석벽',
      x: wx,
      y: wy,
    });
  });
}

// Dungeon Monster Spawns Config
export function getDungeonMonsterConfigs(): EntityState[] {
  return [
    // --- Pack 1: Skeleton Warriors in Corridor ---
    {
      id: 'mob_skel_corridor_1',
      name: '스켈레톤 순찰병 A',
      category: 'monster',
      role: 'monster',
      job: '해골 병사',
      x: 9.5,
      y: -11,
      vx: 0,
      vy: 0,
      dir: 'S',
      speed: 3.2,
      resources: { hp: 130, maxHp: 130, sp: 80, maxSp: 80, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#cbd5e1',
      weaponType: 'slash',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '💀',
    },
    {
      id: 'mob_skel_corridor_2',
      name: '스켈레톤 순찰병 B',
      category: 'monster',
      role: 'monster',
      job: '해골 병사',
      x: 10.5,
      y: -12,
      vx: 0,
      vy: 0,
      dir: 'S',
      speed: 3.2,
      resources: { hp: 130, maxHp: 130, sp: 80, maxSp: 80, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#cbd5e1',
      weaponType: 'slash',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '💀',
    },
    {
      id: 'mob_skel_corridor_3',
      name: '스켈레톤 순찰병 C',
      category: 'monster',
      role: 'monster',
      job: '해골 병사',
      x: 10.0,
      y: -14,
      vx: 0,
      vy: 0,
      dir: 'S',
      speed: 3.2,
      resources: { hp: 130, maxHp: 130, sp: 80, maxSp: 80, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#cbd5e1',
      weaponType: 'slash',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '💀',
    },

    // --- Pack 2: Skeleton Archers & Knight in West Chamber ---
    {
      id: 'mob_skel_archer_1',
      name: '스켈레톤 궁수 A',
      category: 'monster',
      role: 'monster',
      job: '해골 명사수',
      x: 3.5,
      y: -12,
      vx: 0,
      vy: 0,
      dir: 'E',
      speed: 3.0,
      resources: { hp: 110, maxHp: 110, sp: 60, maxSp: 60, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#cbd5e1',
      weaponType: 'thrust',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '🏹',
    },
    {
      id: 'mob_skel_archer_2',
      name: '스켈레톤 궁수 B',
      category: 'monster',
      role: 'monster',
      job: '해골 명사수',
      x: 4.5,
      y: -14,
      vx: 0,
      vy: 0,
      dir: 'E',
      speed: 3.0,
      resources: { hp: 110, maxHp: 110, sp: 60, maxSp: 60, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#cbd5e1',
      weaponType: 'thrust',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '🏹',
    },
    {
      id: 'mob_skel_knight_1',
      name: '크립트 해골 기사',
      category: 'monster',
      role: 'monster',
      job: '해골 기사',
      x: 6.0,
      y: -11,
      vx: 0,
      vy: 0,
      dir: 'E',
      speed: 3.2,
      resources: { hp: 200, maxHp: 200, sp: 90, maxSp: 90, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#94a3b8',
      weaponType: 'slash',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '🛡️',
    },

    // --- Pack 3: Crypt Ghouls & Goblins in East Chamber ---
    {
      id: 'mob_ghoul_1',
      name: '크립트 구울 A',
      category: 'monster',
      role: 'monster',
      job: '언데드 구울',
      x: 16.0,
      y: -12,
      vx: 0,
      vy: 0,
      dir: 'W',
      speed: 3.5,
      resources: { hp: 140, maxHp: 140, sp: 80, maxSp: 80, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#047857',
      weaponType: 'slash',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '🧟',
    },
    {
      id: 'mob_ghoul_2',
      name: '크립트 구울 B',
      category: 'monster',
      role: 'monster',
      job: '언데드 구울',
      x: 17.5,
      y: -13,
      vx: 0,
      vy: 0,
      dir: 'W',
      speed: 3.5,
      resources: { hp: 140, maxHp: 140, sp: 80, maxSp: 80, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#047857',
      weaponType: 'slash',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '🧟',
    },

    // --- Pack 4: Ancient Guardians in Great Hall ---
    {
      id: 'mob_guardian_troll_1',
      name: '고대 수호자 트롤 A',
      category: 'monster',
      role: 'monster',
      job: '대전당 정예 수호자',
      x: 8.5,
      y: -19,
      vx: 0,
      vy: 0,
      dir: 'S',
      speed: 2.2,
      resources: { hp: 450, maxHp: 450, sp: 120, maxSp: 120, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#065f46',
      weaponType: 'crush',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '👹',
    },
    {
      id: 'mob_guardian_troll_2',
      name: '고대 수호자 트롤 B',
      category: 'monster',
      role: 'monster',
      job: '대전당 정예 수호자',
      x: 12.0,
      y: -19,
      vx: 0,
      vy: 0,
      dir: 'S',
      speed: 2.2,
      resources: { hp: 450, maxHp: 450, sp: 120, maxSp: 120, mp: 0, maxMp: 0, conc: 0, maxConc: 0 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#065f46',
      weaponType: 'crush',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '👹',
    },

    // --- EPIC BOSS: Ancient Flame Dragon in North Shrine ---
    {
      id: 'mob_dragon_boss',
      name: '고대 화염룡 (Dragon Boss)',
      category: 'monster',
      role: 'monster',
      job: '최종 보스 (세계수 수호룡)',
      x: 10.0,
      y: -27,
      vx: 0,
      vy: 0,
      dir: 'S',
      speed: 2.8,
      resources: { hp: 1200, maxHp: 1200, sp: 300, maxSp: 300, mp: 500, maxMp: 500, conc: 100, maxConc: 100 },
      buffs: [],
      cc: null,
      cast: null,
      isDead: false,
      color: '#b91c1c',
      weaponType: 'crush',
      cooldowns: {},
      threatTable: {},
      targetId: null,
      inCombat: false,
      avatarIcon: '🐲',
    },
  ];
}

// ============================================================================
// CEMETERY CATACOMBS MONSTER CONFIGS (공동묘지 지하 1, 2, 3층 몬스터 & 3대 보스)
// ============================================================================
export function getCemeteryMonsterConfigs(floor: 1 | 2 | 3): EntityState[] {
  if (floor === 1) {
    // 지하 1층: 해골 병사, 해골 궁수, 부패한 좀비
    return [
      {
        id: 'mob_skel_b1_1', name: '해골 병사 A', category: 'monster', role: 'monster', job: '해골 검사',
        x: 18, y: 20, vx: 0, vy: 0, dir: 'S', speed: 3.2,
        resources: { hp: 180, maxHp: 180, sp: 150, maxSp: 150, mp: 50, maxMp: 50, conc: 100, maxConc: 100 },
        buffs: [], cc: null, cast: null, isDead: false, color: '#94a3b8', weaponType: 'slash',
        cooldowns: {}, threatTable: {}, targetId: null, inCombat: false, avatarIcon: '💀',
        appearance: { skinColor: '#f1f5f9', hairStyle: 'short', hairColor: '#000', facialFeature: 'none', eyeColor: '#ef4444', primaryArmorColor: '#334155', secondaryArmorColor: '#0f172a', capeColor: '#000', showHelm: true, bodyScale: 1.0 }
      },
      {
        id: 'mob_skel_b1_2', name: '해골 병사 B', category: 'monster', role: 'monster', job: '해골 검사',
        x: 22, y: 24, vx: 0, vy: 0, dir: 'E', speed: 3.2,
        resources: { hp: 180, maxHp: 180, sp: 150, maxSp: 150, mp: 50, maxMp: 50, conc: 100, maxConc: 100 },
        buffs: [], cc: null, cast: null, isDead: false, color: '#94a3b8', weaponType: 'slash',
        cooldowns: {}, threatTable: {}, targetId: null, inCombat: false, avatarIcon: '💀',
        appearance: { skinColor: '#f1f5f9', hairStyle: 'short', hairColor: '#000', facialFeature: 'none', eyeColor: '#ef4444', primaryArmorColor: '#334155', secondaryArmorColor: '#0f172a', capeColor: '#000', showHelm: true, bodyScale: 1.0 }
      },
      {
        id: 'mob_skel_arch_b1_1', name: '해골 궁수', category: 'monster', role: 'monster', job: '해골 명사수',
        x: 28, y: 18, vx: 0, vy: 0, dir: 'W', speed: 3.6,
        resources: { hp: 140, maxHp: 140, sp: 120, maxSp: 120, mp: 80, maxMp: 80, conc: 100, maxConc: 100 },
        buffs: [], cc: null, cast: null, isDead: false, color: '#cbd5e1', weaponType: 'thrust',
        cooldowns: {}, threatTable: {}, targetId: null, inCombat: false, avatarIcon: '🏹',
        appearance: { skinColor: '#f1f5f9', hairStyle: 'short', hairColor: '#000', facialFeature: 'none', eyeColor: '#38bdf8', primaryArmorColor: '#475569', secondaryArmorColor: '#0f172a', capeColor: '#000', showHelm: false, bodyScale: 1.0 }
      },
      {
        id: 'mob_zombie_b1_1', name: '부패한 좀비 A', category: 'monster', role: 'monster', job: '부패자',
        x: 45, y: 20, vx: 0, vy: 0, dir: 'S', speed: 2.2,
        resources: { hp: 320, maxHp: 320, sp: 200, maxSp: 200, mp: 40, maxMp: 40, conc: 100, maxConc: 100 },
        buffs: [], cc: null, cast: null, isDead: false, color: '#15803d', weaponType: 'crush',
        cooldowns: {}, threatTable: {}, targetId: null, inCombat: false, avatarIcon: '🧟',
        appearance: { skinColor: '#4ade80', hairStyle: 'long', hairColor: '#1e293b', facialFeature: 'none', eyeColor: '#facc15', primaryArmorColor: '#166534', secondaryArmorColor: '#14532d', capeColor: '#000', showHelm: false, bodyScale: 1.2 }
      },
      {
        id: 'mob_zombie_b1_2', name: '부패한 좀비 B', category: 'monster', role: 'monster', job: '부패자',
        x: 48, y: 25, vx: 0, vy: 0, dir: 'N', speed: 2.2,
        resources: { hp: 320, maxHp: 320, sp: 200, maxSp: 200, mp: 40, maxMp: 40, conc: 100, maxConc: 100 },
        buffs: [], cc: null, cast: null, isDead: false, color: '#15803d', weaponType: 'crush',
        cooldowns: {}, threatTable: {}, targetId: null, inCombat: false, avatarIcon: '🧟',
        appearance: { skinColor: '#4ade80', hairStyle: 'long', hairColor: '#1e293b', facialFeature: 'none', eyeColor: '#facc15', primaryArmorColor: '#166534', secondaryArmorColor: '#14532d', capeColor: '#000', showHelm: false, bodyScale: 1.2 }
      }
    ];
  } else if (floor === 2) {
    // 지하 2층: 비명지르는 망령, 동굴 흡혈박쥐, 묘지 맹독충
    return [
      {
        id: 'mob_wraith_b2_1', name: '비명지르는 망령', category: 'monster', role: 'monster', job: '원혼 사령',
        x: 20, y: 42, vx: 0, vy: 0, dir: 'E', speed: 4.2,
        resources: { hp: 220, maxHp: 220, sp: 150, maxSp: 150, mp: 300, maxMp: 300, conc: 100, maxConc: 100 },
        buffs: [], cc: null, cast: null, isDead: false, color: '#38bdf8', weaponType: 'pure',
        cooldowns: {}, threatTable: {}, targetId: null, inCombat: false, avatarIcon: '👻',
        appearance: { skinColor: '#38bdf8', hairStyle: 'long', hairColor: '#bae6fd', facialFeature: 'none', eyeColor: '#fff', primaryArmorColor: '#0369a1', secondaryArmorColor: '#082f49', capeColor: '#0284c7', showHelm: false, bodyScale: 1.1 }
      },
      {
        id: 'mob_bat_b2_1', name: '동굴 흡혈박쥐 A', category: 'monster', role: 'monster', job: '흡혈 야수',
        x: 46, y: 22, vx: 0, vy: 0, dir: 'S', speed: 5.0,
        resources: { hp: 120, maxHp: 120, sp: 100, maxSp: 100, mp: 100, maxMp: 100, conc: 100, maxConc: 100 },
        buffs: [], cc: null, cast: null, isDead: false, color: '#a855f7', weaponType: 'thrust',
        cooldowns: {}, threatTable: {}, targetId: null, inCombat: false, avatarIcon: '🦇',
        appearance: { skinColor: '#581c87', hairStyle: 'short', hairColor: '#3b0764', facialFeature: 'none', eyeColor: '#ef4444', primaryArmorColor: '#6b21a8', secondaryArmorColor: '#3b0764', capeColor: '#000', showHelm: false, bodyScale: 0.9 }
      },
      {
        id: 'mob_bat_b2_2', name: '동굴 흡혈박쥐 B', category: 'monster', role: 'monster', job: '흡혈 야수',
        x: 50, y: 25, vx: 0, vy: 0, dir: 'W', speed: 5.0,
        resources: { hp: 120, maxHp: 120, sp: 100, maxSp: 100, mp: 100, maxMp: 100, conc: 100, maxConc: 100 },
        buffs: [], cc: null, cast: null, isDead: false, color: '#a855f7', weaponType: 'thrust',
        cooldowns: {}, threatTable: {}, targetId: null, inCombat: false, avatarIcon: '🦇',
        appearance: { skinColor: '#581c87', hairStyle: 'short', hairColor: '#3b0764', facialFeature: 'none', eyeColor: '#ef4444', primaryArmorColor: '#6b21a8', secondaryArmorColor: '#3b0764', capeColor: '#000', showHelm: false, bodyScale: 0.9 }
      },
      {
        id: 'mob_beetle_b2_1', name: '묘지 맹독충', category: 'monster', role: 'monster', job: '산성 갑충',
        x: 32, y: 25, vx: 0, vy: 0, dir: 'N', speed: 3.0,
        resources: { hp: 160, maxHp: 160, sp: 120, maxSp: 120, mp: 50, maxMp: 50, conc: 100, maxConc: 100 },
        buffs: [], cc: null, cast: null, isDead: false, color: '#b45309', weaponType: 'crush',
        cooldowns: {}, threatTable: {}, targetId: null, inCombat: false, avatarIcon: '🪲',
        appearance: { skinColor: '#78350f', hairStyle: 'short', hairColor: '#451a03', facialFeature: 'none', eyeColor: '#84cc16', primaryArmorColor: '#92400e', secondaryArmorColor: '#451a03', capeColor: '#000', showHelm: false, bodyScale: 0.8 }
      }
    ];
  } else {
    // 지하 3층: 3대 보스 몬스터 (3배 크기, 이그나르급 체력 1200 ~ 1500 HP)
    return [
      // 1. 북서 보스: 해골화된 루나리스 병사 (Skeletonized Lunaris Soldier)
      // ※ 루나리스 왕국에서 가장 약한 최하급 병사이나, 솔라리스 대륙에서는 거대한 3배 크기의 공포스러운 존재
      {
        id: 'boss_cemetery_skel_king',
        name: '💀 [BOSS] 해골화된 루나리스 병사',
        category: 'monster',
        role: 'monster',
        job: '루나리스 최하급 척후병 (해골화)',
        x: 26.0,
        y: 26.0,
        vx: 0,
        vy: 0,
        dir: 'S',
        speed: 3.4,
        resources: { hp: 1350, maxHp: 1350, sp: 400, maxSp: 400, mp: 300, maxMp: 300, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#dc2626',
        weaponType: 'slash',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '💀',
        appearance: {
          skinColor: '#f8fafc',
          hairStyle: 'short',
          hairColor: '#000000',
          facialFeature: 'none',
          eyeColor: '#ef4444',
          primaryArmorColor: '#1e293b',
          secondaryArmorColor: '#0f172a',
          capeColor: '#991b1b',
          showHelm: true,
          bodyScale: 3.0 // 캐릭터의 3배 거대 크기!
        }
      },
      // 2. 북동 보스: 좀비화된 루나리스 병사 (Zombified Lunaris Soldier)
      // ※ 루나리스 왕국 최전방 육벽 병사. 살점과 피를 탐하는 3배 크기의 거체
      {
        id: 'boss_cemetery_abomination',
        name: '🧟 [BOSS] 좀비화된 루나리스 병사',
        category: 'monster',
        role: 'monster',
        job: '루나리스 최하급 보병 (좀비화)',
        x: 102.0,
        y: 26.0,
        vx: 0,
        vy: 0,
        dir: 'S',
        speed: 2.6,
        resources: { hp: 1500, maxHp: 1500, sp: 500, maxSp: 500, mp: 200, maxMp: 200, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#16a34a',
        weaponType: 'crush',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '🧟',
        appearance: {
          skinColor: '#22c55e',
          hairStyle: 'long',
          hairColor: '#0f172a',
          facialFeature: 'none',
          eyeColor: '#facc15',
          primaryArmorColor: '#14532d',
          secondaryArmorColor: '#052e16',
          capeColor: '#166534',
          showHelm: false,
          bodyScale: 3.0 // 캐릭터의 3배 거대 크기!
        }
      },
      // 3. 남서 보스: 흡혈박쥐 (Vampire Bat)
      // ※ 루나리스 군단이 정찰용으로 사역하는 거대 흡혈 마수
      {
        id: 'boss_cemetery_vampire_bat',
        name: '🦇 [BOSS] 흡혈박쥐',
        category: 'monster',
        role: 'monster',
        job: '루나리스 정찰 사역마 (거대 박쥐)',
        x: 26.0,
        y: 102.0,
        vx: 0,
        vy: 0,
        dir: 'N',
        speed: 4.8,
        resources: { hp: 1200, maxHp: 1200, sp: 350, maxSp: 350, mp: 500, maxMp: 500, conc: 100, maxConc: 100 },
        buffs: [],
        cc: null,
        cast: null,
        isDead: false,
        color: '#9333ea',
        weaponType: 'thrust',
        cooldowns: {},
        threatTable: {},
        targetId: null,
        inCombat: false,
        avatarIcon: '🦇',
        appearance: {
          skinColor: '#3b0764',
          hairStyle: 'short',
          hairColor: '#1e1b4b',
          facialFeature: 'none',
          eyeColor: '#f43f5e',
          primaryArmorColor: '#581c87',
          secondaryArmorColor: '#2e1065',
          capeColor: '#701a75',
          showHelm: false,
          bodyScale: 3.0 // 캐릭터의 3배 거대 크기!
        }
      }
    ];
  }
}

// Initialize immediately
initDungeonData();
